import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter, type RouteMap } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  useGamesList,
  useGamesStore,
  type CmsAppShortcutAction,
} from '@nsftx/seven-client-retail-vue-sgp'
import {
  NXGDSNavigationItemState,
  NXGDSTagIndicatorVariants,
  type NXGDSNavigationItemModel,
} from '@nsftx/seven-nxg-design-system'
import { getShortcutKeys, useShortcutsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useNavbarStore } from '@/stores/navbar/navbarStore'
import { ROUTES_NAMES } from '@/router/routesNames'
import { useLastVisitedGame } from '../useLastVisitedGame/useLastVisitedGame'
import { useGameConfigurationStore } from '@/stores/cms/gameConfigurationStore'
import { routes } from '@/router/routes'

interface OperationMenuRouteMeta {
  shortcut: CmsAppShortcutAction
  icon: string
  titleKey: string
}

interface OperationMenuItem extends NXGDSNavigationItemModel {
  routeName?: keyof RouteMap
}

const isOperationMenuRouteMeta = (meta: object): meta is OperationMenuRouteMeta => {
  const { shortcut, icon, titleKey } = meta as OperationMenuRouteMeta

  return Boolean(shortcut && icon && titleKey)
}

export const useMainMenu = () => {
  const route = useRoute()
  const router = useRouter()
  const { gameLinks } = storeToRefs(useGamesStore())
  const { activeMenuItemIndex } = storeToRefs(useNavbarStore())
  const { setLastVisitedGame } = useLastVisitedGame()
  const { formattedGames } = useGamesList(gameLinks)
  const { getByGameId } = useGameConfigurationStore()
  const { getNavigateGameShortcutById, getAppShortcutsByAction } = useShortcutsStore()
  const { t } = useI18n()

  const gamesItems = computed<NXGDSNavigationItemModel[]>(() =>
    formattedGames.value.map((game, index) => {
      const gameShortcut = getNavigateGameShortcutById(game.gameId)
      const tags = gameShortcut
        ? getShortcutKeys(gameShortcut.shortcut[0]).map((label) => ({
            label,
            variant: NXGDSTagIndicatorVariants.subtle,
          }))
        : []
      const state =
        activeMenuItemIndex.value === index
          ? NXGDSNavigationItemState.active
          : NXGDSNavigationItemState.default

      return {
        uuid: game.gameDisplayId,
        title: game.gameDisplayName,
        iconName: getByGameId(game.gameId)?.icon ?? '',
        tags,
        state,
      }
    }),
  )

  const operationNavigationItems = computed(() => {
    const fromRoutes = routes.flatMap((route) => {
      if (!('meta' in route) || !route.name || !isOperationMenuRouteMeta(route.meta)) {
        return []
      }

      const { shortcut, icon, titleKey } = route.meta

      return [
        {
          uuid: route.path,
          name: route.name,
          iconName: icon,
          shortcutAction: shortcut,
          title: t(titleKey),
        },
      ]
    })

    return fromRoutes
  })

  const operationItems = computed(() =>
    operationNavigationItems.value.map((item) => {
      const appShortcuts = getAppShortcutsByAction(item.shortcutAction)
      const tags = appShortcuts
        ? getShortcutKeys(appShortcuts.shortcut[0]).map((label) => ({
            label,
            variant: NXGDSTagIndicatorVariants.subtle,
          }))
        : []
      const state = route.matched.some((record) => record.name === item.name)
        ? NXGDSNavigationItemState.active
        : NXGDSNavigationItemState.default

      return {
        ...item,
        tags,
        state,
      }
    }),
  )

  const navigateToGame = async (item: NXGDSNavigationItemModel) => {
    await router.push({ name: ROUTES_NAMES.game, params: { gameId: item.uuid } })
    setLastVisitedGame(item.uuid, item.title)
  }

  const navigateToOperation = (item: OperationMenuItem) => {
    router.push({ path: item.uuid })
  }

  return {
    gamesItems,
    operationItems,
    navigateToGame,
    navigateToOperation,
  }
}
